import React from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import LoadingHint from 'tailwind-components/LoadingHint/LoadingHint';
import TablePager from 'tailwind-components/TablePager/TablePager';
import { cn } from 'lib/utils';

/**
 * bordered card that frames a table: title and row count on the left of the header,
 * loading state and pagination on the right. the pager lives here rather than under
 * the table so both views share one place for it, and the hint sits next to it so the
 * feedback appears where the user just clicked.
 * @param title - section title, e.g. 'Agents'
 * @param count - number of matching rows across all pages
 * @param countLabel - singular noun for the count, pluralized with an 's'
 * @param page - current 1-indexed page
 * @param totalPages - total pages reported by the api
 * @param onPageChange - called with the page to move to
 * @param isRefreshing - true while a fetch runs with rows already on screen
 * @param isRetrying - true while that fetch is being retried after a failure
 * @param refreshError - message shown when a refetch failed and the rows are stale
 * @param onRetryRefreshError - retries the failed fetch; omit to hide the retry action
 * @param children - the table
 */
export default function TableCard({
  title,
  count,
  countLabel,
  page,
  totalPages,
  onPageChange,
  isRefreshing = false,
  isRetrying = false,
  refreshError = null,
  onRetryRefreshError,
  children
}) {
  const suffix = count === 1 ? countLabel : `${countLabel}s`;

  return (
    <section className='overflow-hidden rounded-xl border border-grey-lighter bg-white'>
      <header className='flex flex-wrap items-center justify-between gap-3 px-5 py-4'>
        <div className='flex items-baseline gap-3'>
          <h2 className='text-xl font-bold text-black m-0 leading-none'>
            {title}
          </h2>
          <span className='text-[length:--font-size-body] text-grey-light'>
            {count} {suffix}
          </span>
        </div>
        <div className='flex items-center gap-4'>
          {isRefreshing && <LoadingHint isRetrying={isRetrying} />}
          <TablePager
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </header>

      {refreshError && (
        <div className='flex flex-wrap items-center gap-2 px-5 pb-3'>
          <p className='m-0 text-xs text-semantic-danger'>{refreshError}</p>
          {onRetryRefreshError && (
            <Button
              variant='default'
              outlined
              size='xxsmall'
              rounded='medium'
              text='Retry'
              onClick={onRetryRefreshError}
            />
          )}
        </div>
      )}

      {/* the rows stay visible but dimmed while they are stale, so it is obvious
          they do not match the page that was just requested */}
      <div className='relative'>
        <div
          aria-busy={isRefreshing}
          className={cn('transition-opacity', isRefreshing && 'opacity-50')}
        >
          {children}
        </div>
        {/* pinned near the top so it stays on screen for tall tables; the header
            hint alone is easy to miss at a glance */}
        {isRefreshing && (
          <div className='pointer-events-none absolute inset-x-0 top-0 flex justify-center pt-14'>
            <span
              role='status'
              className='flex items-center gap-2 rounded-full border border-grey-lighter bg-white/95 px-4 py-2 shadow-md'
            >
              <Loader2
                className='size-5 animate-spin text-primary-darkened'
                aria-hidden
              />
              <span className='text-sm font-medium text-grey-dark'>
                {isRetrying ? 'Retrying…' : 'Loading…'}
              </span>
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

TableCard.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  countLabel: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  isRefreshing: PropTypes.bool,
  isRetrying: PropTypes.bool,
  refreshError: PropTypes.string,
  onRetryRefreshError: PropTypes.func,
  children: PropTypes.node
};
